import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { FullPageSpinner } from "@/components/Spinner";
import { StreamingChannelForm } from "@/components/forms/StreamingChannelForm";
import { StreamingChannelInput } from "@/components/forms/validation/streaming-channel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getFragmentData, graphql } from "@/gen";

const EditStreamingChannel_StreamingChannel = graphql(`
    fragment EditStreamingChannel_StreamingChannel on StreamingChannel {
        id
        externalChannelHandle
        externalChannelId
        provider
        imageId
    }
`);

const useStreamingChannel = (id: string) => {
    const [result] = useQuery({
        query: graphql(`
            query streamingChannelQuery($id: UUID!) {
                streamingChannel(id: $id) {
                    ...EditStreamingChannel_StreamingChannel
                }
            }
        `),
        variables: { id },
    });

    const streamingChannel = result.data?.streamingChannel
        ? getFragmentData(EditStreamingChannel_StreamingChannel, result.data.streamingChannel)
        : null;

    return {
        streamingChannel,
        loading: result.fetching,
        error: result.error,
    };
};

const updateStreamingChannelMutation = graphql(`
    mutation updateStreamingChannel($input: UpdateStreamingChannelInput!) {
        updateStreamingChannel(input: $input) {
            id
            externalChannelHandle
            externalChannelId
            provider
            imageId
            createdAt
            updatedAt
        }
    }
`);

const deleteStreamingChannelMutation = graphql(`
    mutation deleteStreamingChannel($id: UUID!) {
        deleteStreamingChannel(id: $id)
    }
`);

const EditStreamingChannelRoute = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { streamingChannel, loading, error } = useStreamingChannel(id!);
    const [_updateRes, updateStreamingChannel] = useMutation(updateStreamingChannelMutation);
    const [_deleteRes, deleteStreamingChannel] = useMutation(deleteStreamingChannelMutation);

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        return <div className="mb-4 w-full space-y-4 py-4">Error {error.message}</div>;
    }

    if (!streamingChannel) {
        return <div className="mb-4 w-full space-y-4 py-4">Streaming channel not found</div>;
    }

    const onSubmit: SubmitHandler<StreamingChannelInput> = async input => {
        setIsSubmitting(true);
        const result = await updateStreamingChannel({
            input: {
                id: streamingChannel.id,
                externalChannelHandle: input.externalChannelHandle,
                externalChannelId: input.externalChannelId,
                provider: input.provider,
                imageId: input.imageId,
            },
        });

        setIsSubmitting(false);

        if (result.error) {
            toast.error("Unable to update streaming channel", {
                description: result.error.message,
            });
        } else {
            toast.success("Streaming channel was updated");
            navigate("/streaming-channels");
        }
    };

    const onDelete = async () => {
        if (
            !confirm(
                "Are you sure you want to delete this streaming channel? This action cannot be undone."
            )
        ) {
            return;
        }

        const result = await deleteStreamingChannel({ id: id! });

        if (result.error) {
            toast.error("Unable to delete streaming channel", {
                description: result.error.message,
            });
        } else {
            toast.success("Streaming channel was deleted");
            navigate("/streaming-channels");
        }
    };

    const defaultValues: StreamingChannelInput = {
        externalChannelHandle: streamingChannel.externalChannelHandle,
        externalChannelId: streamingChannel.externalChannelId,
        provider: streamingChannel.provider,
        imageId: streamingChannel.imageId,
    };

    return (
        <div className="m-4">
            <Card className="bg-brand-white text-brand-black rounded border-none">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Edit Streaming Channel</CardTitle>
                        <div className="space-x-2">
                            <Button
                                variant="outline"
                                onClick={() => navigate("/streaming-channels")}
                            >
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={onDelete}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <StreamingChannelForm
                        onSubmit={onSubmit}
                        defaultValues={defaultValues}
                        isSubmitting={isSubmitting}
                    />
                </CardContent>
            </Card>
        </div>
    );
};

export default EditStreamingChannelRoute;
